import {memo, useContext, useRef, useState} from 'react';

import styled from 'styled-components';

import {addressKeyFromParts} from '~/shared/utils/address';
import {AddressViewValuesMap, LocationTypes, AddressFinderVariant} from '~/shared/consts/addressConsts';
import _AddressFinder from '~/shared/components/AddressFinder';
import {getLocalizationService} from '~/shared/services/localisationService';
import {AddressFinderLocation, FormNamesEnum} from '~/shared/services/analytics/analyticsModels';
import {trackEvent} from '~/shared/services/analytics';
import useChat from '~/shared/hooks/useChat';
import {body16Normal} from '~/shared/theme/typography';
import {flipOnLTR} from '~/shared/theme/flipOnLTR';
import PieButton from '~/shared/theme/pie/PieButton';

import {
  BlockHeader,
  TitleText,
  BackIcon,
  AddressDropdownButton as BackButton,
  FormContainer,
  FormText,
  ButtonBar,
  AddAddressActionButton,
} from './styled.addressDropdown';
import {AddressDropdownContext} from './context';

const AddressFinder = styled(_AddressFinder)`
  &::after {
    margin: 11px;
  }
`;

const StyledButtonBar = styled(ButtonBar)`
  margin-top: unset;
`;

const StyledFormContainer = styled(FormContainer)`
  margin-bottom: auto;
`;

const CantFindAddressError = styled.div`
  display: flex;
  padding: 0 25px;
`;

const ErrorText = styled.div`
  ${body16Normal}
  color: ${({theme}) => theme.colors.contentError};
  ${flipOnLTR`
    margin-left: 8px;  
  `}
`;

const StyledPieButton = styled(PieButton)`
  ${body16Normal}
  color: ${({theme}) => theme.colors.contentLink};
`;

const UpsertLocalAddressBlock = ({
  editedAddress,
  onSubmit,
  changeAddress,
  titleText,
  subTitleText,
  onViewChange,
  buttonText,
  locationType = LocationTypes.RESIDENTIAL,
}) => {
  const addressRef = useRef(editedAddress || null);
  const [disableSubmit, setDisableSubmit] = useState(true);
  const {isMaxMobile} = useContext(AddressDropdownContext);
  const [cantFindAddressError, setCantFindAddressError] = useState(false);
  const {openChat} = useChat();

  const {t} = getLocalizationService();

  const handleContactUsClick = () => {
    trackEvent('addressErrorMessageClick', {
      errorType: t('cant_find_your_address'),
      formName: FormNamesEnum.ADDRESS,
      onCheckout: false,
    });
    openChat();
  };

  const handleAddressChange = ({address: newAddress}) => {
    addressRef.current = newAddress;
    setDisableSubmit(!newAddress);
  };

  const handleAddButtonClick = async () => {
    if (!addressRef.current) {
      return;
    }

    const fullAddress = {
      locationType,
      addressKey: addressKeyFromParts(addressRef.current),
      ...addressRef.current,
    };

    if (onSubmit) {
      await onSubmit(fullAddress);
    }

    changeAddress({address: fullAddress});
  };

  return (
    <>
      <BlockHeader>
        <TitleText>{titleText}</TitleText>
        <BackButton data-test-id="addressDropdownBackBtn" onClick={() => onViewChange(AddressViewValuesMap.MAIN)} aria-label={t('back')}>
          <BackIcon />
        </BackButton>
      </BlockHeader>

      <StyledFormContainer>
        <FormText>{subTitleText}</FormText>
        <AddressFinder
          variant={AddressFinderVariant.PIE}
          location={AddressFinderLocation.ADDRESS_DROPDOWN}
          disableAutoFocus
          placeholder={t('street_house_number_city')}
          onChange={incAddress => handleAddressChange({address: incAddress})}
          submit={handleAddButtonClick}
          disableSubmit={disableSubmit}
          setDisableSubmit={setDisableSubmit}
          isAddAddressMode
          isMaxMobile={isMaxMobile}
          setCantFindAddressError={setCantFindAddressError}
        />
      </StyledFormContainer>
      {cantFindAddressError && (
        <CantFindAddressError>
          <ErrorText>
            {t('cant_find_your_address')}
          </ErrorText>
          <StyledPieButton onClick={handleContactUsClick}>
            {t('contact_us')}
          </StyledPieButton>
        </CantFindAddressError>
      )}
      <StyledButtonBar center>
        <AddAddressActionButton
          data-test-id="submitNewAddressBtn"
          disabled={disableSubmit}
          onClick={handleAddButtonClick}
        >
          {buttonText}
        </AddAddressActionButton>
      </StyledButtonBar>
    </>
  );
};

export default memo(UpsertLocalAddressBlock);
